import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { User, Trophy, Zap, Wallet, Award, Calendar, Target, Share2, Flame, MessageCircle, Palette, Check, Bell, BellOff, Sprout, Crown, Brain, Rocket, TrendingUp, Landmark, Bot, Star, Search, Sparkles, Gem, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { enablePush, disablePush } from '../lib/push';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeInfo = {
  newcomer: { label: 'Newcomer', color: '#C0975C', icon: Sprout },
  first_quiz: { label: 'First Steps', color: '#C0975C', icon: Target },
  quiz_streak_3: { label: 'On Fire', color: '#FF6B35', icon: Flame },
  quiz_streak_7: { label: 'Unstoppable', color: '#C0975C', icon: Zap },
  quiz_streak_30: { label: 'Legend', color: '#0A1F44', icon: Crown },
  quiz_master: { label: 'Quiz Master', color: '#0A1F44', icon: Brain },
  budget_boss: { label: 'Budget Boss', color: '#C0975C', icon: Wallet },
  hustler: { label: 'Hustler', color: '#C0975C', icon: Rocket },
  investor: { label: 'Investor', color: '#C0C0C0', icon: TrendingUp },
  saver: { label: 'Super Saver', color: '#00A86B', icon: Landmark },
  mentor_friend: { label: "Mentor's Friend", color: '#7B2CBF', icon: Bot },
  social_star: { label: 'Social Star', color: '#C0975C', icon: Star },
  product_explorer: { label: 'Explorer', color: '#C0975C', icon: Search },
  top_10: { label: 'Top 10', color: '#C0975C', icon: Trophy },
  level_5: { label: 'Rising Star', color: '#C0975C', icon: Sparkles },
  level_10: { label: 'Financial Pro', color: '#0A1F44', icon: Gem },
};

const AVATAR_COLORS = ['#C0975C', '#0A1F44', '#C0975C', '#C0975C', '#FF6B35', '#7B2CBF', '#00FF88'];

export default function Profile() {
  const { user, token, refreshUser } = useAuth();
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState(user?.avatar_color || '#C0975C');
  const [reminder, setReminder] = useState({ quiz_reminder_email: false, quiz_reminder_push: false, vapid_public_key: '' });
  const [reminderBusy, setReminderBusy] = useState(false);

  useEffect(() => {
    loadReminder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReminder = async () => {
    try {
      const res = await axios.get(`${API}/notifications/quiz-reminder`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReminder(res.data);
    } catch (error) {
      console.log('Reminder settings load failed:', error);
    }
  };

  const toggleEmailReminder = async () => {
    const next = !reminder.quiz_reminder_email;
    setReminderBusy(true);
    try {
      const res = await axios.patch(`${API}/notifications/quiz-reminder`, { quiz_reminder_email: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReminder((r) => ({ ...r, ...res.data }));
      toast.success(next ? 'Email reminder turned on' : 'Email reminder turned off');
    } catch (error) {
      toast.error('Could not update reminder');
    } finally {
      setReminderBusy(false);
    }
  };

  const togglePushReminder = async () => {
    setReminderBusy(true);
    try {
      if (reminder.quiz_reminder_push) {
        await disablePush(token);
        setReminder((r) => ({ ...r, quiz_reminder_push: false }));
        toast.success('Push reminder turned off');
      } else {
        await enablePush(token, reminder.vapid_public_key);
        setReminder((r) => ({ ...r, quiz_reminder_push: true }));
        toast.success('Push reminder turned on');
      }
    } catch (error) {
      const msg = error.message === 'denied'
        ? 'Notifications are blocked. Allow them in your browser or phone settings.'
        : error.message === 'unsupported'
        ? 'This browser does not support push notifications. On iPhone, add the app to your home screen first.'
        : error.message === 'insecure'
        ? 'Push notifications need a secure connection.'
        : 'Could not update push notifications.';
      toast.error(msg);
    } finally {
      setReminderBusy(false);
    }
  };

  const sendTestReminder = async () => {
    setReminderBusy(true);
    try {
      const res = await axios.post(`${API}/notifications/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Test sent (push: ${res.data.push_sent}, email: ${res.data.email_sent ? 'yes' : 'no'})`);
    } catch (error) {
      toast.error('Could not send test reminder');
    } finally {
      setReminderBusy(false);
    }
  };

  const [privacy, setPrivacy] = useState({ marketing_consent: false, requests: [] });
  const [privacyBusy, setPrivacyBusy] = useState(false);

  useEffect(() => {
    loadPrivacy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPrivacy = async () => {
    try {
      const res = await axios.get(`${API}/privacy/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setPrivacy(res.data);
    } catch (error) { /* ignore */ }
  };

  const toggleMarketing = async () => {
    const next = !privacy.marketing_consent;
    setPrivacyBusy(true);
    try {
      await axios.patch(`${API}/privacy/marketing`, { marketing_consent: next }, { headers: { Authorization: `Bearer ${token}` } });
      setPrivacy((p) => ({ ...p, marketing_consent: next }));
      toast.success(next ? 'Marketing preference turned on' : 'Marketing preference turned off');
    } catch (error) { toast.error('Could not update preference'); } finally { setPrivacyBusy(false); }
  };

  const requestData = async (type) => {
    if (type === 'delete' && !window.confirm('Request deletion of your account and personal data? Our team will action this and contact you.')) return;
    setPrivacyBusy(true);
    try {
      await axios.post(`${API}/privacy/data-request`, { type }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(type === 'delete' ? 'Deletion request logged. We will be in touch.' : 'Data export request logged. We will be in touch.');
      loadPrivacy();
    } catch (error) { toast.error('Could not submit request'); } finally { setPrivacyBusy(false); }
  };

  const xpProgress = user ? (user.xp % 100) : 0;
  const xpToNextLevel = 100 - xpProgress;

  const handleShare = async (type, value, message = '') => {
    try {
      const response = await axios.post(`${API}/share`, {
        score_type: type,
        score_value: value,
        message: message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await navigator.clipboard.writeText(response.data.share_text);
      toast.success('Copied to clipboard. Share it on social media.');
      
      if (response.data.badges_earned?.length > 0) {
        toast.success(`New badge earned: ${response.data.badges_earned[0]}`);
        await refreshUser();
      }
    } catch (error) {
      toast.error('Failed to generate share text');
    }
  };

  const handleWhatsAppShare = async (type) => {
    try {
      const response = await axios.post(`${API}/share/whatsapp`, {
        share_type: type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.open(response.data.whatsapp_url, '_blank');
      
      if (response.data.badges_earned?.length > 0) {
        toast.success(`New badge earned: ${response.data.badges_earned[0]}`);
        await refreshUser();
      }
    } catch (error) {
      toast.error('Failed to generate WhatsApp share');
    }
  };

  const handleAvatarUpdate = async (color) => {
    try {
      await axios.put(`${API}/avatar`, {
        avatar_color: color,
        avatar_style: 'default'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedColor(color);
      setShowAvatarPicker(false);
      toast.success('Avatar updated!');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to update avatar');
    }
  };

  const stats = [
    { label: 'Total XP', value: user?.xp?.toLocaleString() || 0, icon: Zap, color: '#C0975C' },
    { label: 'Level', value: user?.level || 1, icon: Trophy, color: '#0A1F44' },
    { label: 'Fitness Score', value: `${Math.min(100, Math.round((user?.xp || 0) / 20))}/100`, icon: Wallet, color: '#C0975C' },
    { label: 'Quizzes Done', value: user?.completed_quizzes?.length || 0, icon: Target, color: '#C0975C' },
  ];

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="profile">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Profile Header */}
          <div className="neo-card p-8 mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="relative">
                <div 
                  className="w-24 h-24 flex items-center justify-center text-black text-4xl font-black cursor-pointer hover:scale-105 transition-transform"
                  style={{ backgroundColor: user?.avatar_color || selectedColor }}
                  onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                  data-testid="avatar-button"
                >
                  {user?.username?.[0]?.toUpperCase()}
                </div>
                <button 
                  className="absolute -bottom-1 -right-1 w-8 h-8 bg-white/20 flex items-center justify-center hover:bg-white/30"
                  onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                >
                  <Palette className="w-4 h-4" />
                </button>
                
                {/* Avatar Color Picker */}
                {showAvatarPicker && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute top-full mt-2 left-0 neo-card p-4 z-10"
                  >
                    <p className="text-sm text-[#A3A3A3] mb-3">Choose avatar color:</p>
                    <div className="flex gap-2 flex-wrap w-48">
                      {AVATAR_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => handleAvatarUpdate(color)}
                          className="w-10 h-10 flex items-center justify-center hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          data-testid={`avatar-color-${color}`}
                        >
                          {(user?.avatar_color || selectedColor) === color && (
                            <Check className="w-5 h-5 text-black" />
                          )}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
              <div className="text-center sm:text-left flex-1">
                <h1 
                  className="text-3xl font-black mb-1"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {user?.username}
                </h1>
                <p className="text-[#A3A3A3] mb-2">{user?.email}</p>
                <div className="flex items-center justify-center sm:justify-start gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-[#A3A3A3]" />
                    <span className="text-[#A3A3A3]">Age: {user?.age}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-[#C0975C]" />
                    <span className="text-[#C0975C]">Level {user?.level}</span>
                  </span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-[#C0975C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {user?.xp?.toLocaleString()}
                </div>
                <p className="text-[#A3A3A3] text-sm">Total XP</p>
              </div>
            </div>

            {/* XP Progress */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#A3A3A3]">Progress to Level {(user?.level || 1) + 1}</span>
                <span className="text-[#C0975C] font-mono">{xpProgress}/100 XP</span>
              </div>
              <div className="xp-bar h-3">
                <motion.div 
                  className="xp-bar-fill"
                  initial={{ width: 0 }}
                  animate={{ width: `${xpProgress}%` }}
                  transition={{ duration: 1, delay: 0.3 }}
                />
              </div>
              <p className="text-[#A3A3A3] text-sm mt-2">
                {xpToNextLevel} XP needed to reach Level {(user?.level || 1) + 1}
              </p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="neo-card p-5 text-center"
              >
                <stat.icon className="w-6 h-6 mx-auto mb-2" style={{ color: stat.color }} />
                <div className="text-2xl font-black" style={{ color: stat.color, fontFamily: 'Outfit, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-[#A3A3A3] text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Badges */}
          <div className="neo-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-6 h-6 text-[#C0975C]" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Badges
              </h2>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {user?.badges?.map((badge, i) => {
                const info = badgeInfo[badge] || { label: badge, color: '#A3A3A3', icon: Trophy };
                const BadgeIcon = info.icon;
                return (
                  <motion.div
                    key={badge}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className="p-4 text-center bg-white/5 hover:bg-white/10 transition-colors"
                    data-testid={`badge-${badge}`}
                  >
                    <div className="mb-2 flex justify-center"><BadgeIcon className="w-8 h-8" style={{ color: info.color }} /></div>
                    <p className="font-bold text-sm" style={{ color: info.color }}>{info.label}</p>
                  </motion.div>
                );
              })}
              
              {/* Locked Badges */}
              {Object.entries(badgeInfo)
                .filter(([key]) => !user?.badges?.includes(key))
                .slice(0, 3)
                .map(([key, info], i) => (
                  <div
                    key={key}
                    className="p-4 text-center bg-white/5 opacity-40"
                  >
                    <div className="mb-2 flex justify-center"><Lock className="w-8 h-8 text-[#A3A3A3]" /></div>
                    <p className="font-bold text-sm text-[#A3A3A3]">{info.label}</p>
                  </div>
                ))}
            </div>
          </div>

          {/* Streak Card */}
          <div className="neo-card p-6 mt-8 border-[#0A1F44]">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Flame className="w-6 h-6 text-[#0A1F44]" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Daily Streak
                </h2>
              </div>
              <button
                onClick={() => handleShare('streak', user?.daily_streak || 0)}
                className="neo-button-secondary px-3 py-1 text-sm flex items-center gap-1"
                data-testid="share-streak-btn"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-white/5">
                <div className="text-4xl font-black text-[#0A1F44]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {user?.daily_streak || 0}
                </div>
                <div className="text-[#A3A3A3] text-sm">Current Streak</div>
              </div>
              <div className="text-center p-4 bg-white/5">
                <div className="text-4xl font-black text-[#C0975C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {user?.longest_streak || user?.daily_streak || 0}
                </div>
                <div className="text-[#A3A3A3] text-sm">Best Streak</div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-white/5">
              <p className="text-sm text-[#A3A3A3]">
                <span className="text-[#C0975C]">Streak Bonuses:</span> 3 days = +15 XP | 7 days = Badge + 35 XP | 30 days = Legend Badge!
              </p>
            </div>
          </div>

          {/* Share Progress */}
          <div className="neo-card p-6 mt-8">
            <div className="flex items-center gap-3 mb-4">
              <Share2 className="w-6 h-6 text-[#C0975C]" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Share Your Progress
              </h2>
            </div>
            <p className="text-[#A3A3A3] mb-4">Show off your financial skills and earn the Social Star badge!</p>
            
            {/* WhatsApp Sharing */}
            <div className="mb-4 p-4 bg-[#25D366]/10 border border-[#25D366]/30">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                <span className="font-bold text-[#25D366]">Share on WhatsApp</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleWhatsAppShare('level')}
                  className="py-2 px-3 bg-[#25D366] text-white text-sm font-bold hover:bg-[#20BD5A] transition-colors"
                  data-testid="whatsapp-share-level"
                >
                  Share Level
                </button>
                <button
                  onClick={() => handleWhatsAppShare('invite')}
                  className="py-2 px-3 bg-[#25D366] text-white text-sm font-bold hover:bg-[#20BD5A] transition-colors"
                  data-testid="whatsapp-share-invite"
                >
                  Invite Friends
                </button>
              </div>
            </div>

            {/* Copy to Clipboard */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleShare('level', user?.level || 1)}
                className="neo-button-primary py-3 text-sm"
                data-testid="share-level-btn"
              >
                Share Level {user?.level}
              </button>
              <button
                onClick={() => handleShare('badge', user?.badges?.length || 0, user?.badges?.[user?.badges?.length - 1] || 'newcomer')}
                className="neo-button-secondary py-3 text-sm"
                data-testid="share-badges-btn"
              >
                Share Badges ({user?.badges?.length || 0})
              </button>
            </div>
          </div>

          {/* Daily Quiz Reminder */}
          <div className="neo-card p-6 mt-8" data-testid="quiz-reminder-card">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="w-6 h-6 text-[#C0975C]" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Daily Quiz Reminder
              </h2>
            </div>
            <p className="text-[#A3A3A3] mb-5">Get a nudge at 7am each day to complete your 5 minute Financial Quiz.</p>

            <div className="space-y-4">
              {/* Email reminder */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-[#C0975C]" />
                  <div>
                    <p className="font-bold">Email reminder</p>
                    <p className="text-sm text-[#A3A3A3]">A daily email with a link to today's quiz</p>
                  </div>
                </div>
                <button
                  onClick={toggleEmailReminder}
                  disabled={reminderBusy}
                  data-testid="reminder-email-toggle"
                  className={`px-4 py-2 font-bold text-sm rounded-lg transition-colors ${
                    reminder.quiz_reminder_email ? 'bg-[#C0975C] text-[#0A1F44] hover:opacity-90' : 'bg-white/10 text-white hover:bg-white/20'
                  } ${reminderBusy ? 'opacity-50' : ''}`}
                >
                  {reminder.quiz_reminder_email ? 'On' : 'Off'}
                </button>
              </div>

              {/* Push reminder */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  {reminder.quiz_reminder_push ? (
                    <Bell className="w-5 h-5 text-[#C0975C]" />
                  ) : (
                    <BellOff className="w-5 h-5 text-[#A3A3A3]" />
                  )}
                  <div>
                    <p className="font-bold">Push notification</p>
                    <p className="text-sm text-[#A3A3A3]">A banner on this device (allow when asked)</p>
                  </div>
                </div>
                <button
                  onClick={togglePushReminder}
                  disabled={reminderBusy}
                  data-testid="reminder-push-toggle"
                  className={`px-4 py-2 font-bold text-sm rounded-lg transition-colors ${
                    reminder.quiz_reminder_push ? 'bg-[#C0975C] text-[#0A1F44] hover:opacity-90' : 'bg-white/10 text-white hover:bg-white/20'
                  } ${reminderBusy ? 'opacity-50' : ''}`}
                >
                  {reminder.quiz_reminder_push ? 'On' : 'Off'}
                </button>
              </div>

              {(reminder.quiz_reminder_email || reminder.quiz_reminder_push) && (
                <button
                  onClick={sendTestReminder}
                  disabled={reminderBusy}
                  data-testid="reminder-test-btn"
                  className="w-full py-2.5 text-sm font-semibold rounded-lg border border-[#1D3D72] text-white hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  Send me a test now
                </button>
              )}
            </div>
          </div>

          {/* Privacy & Data */}
          <div className="neo-card p-6 mt-8" data-testid="privacy-data-card">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-[#C0975C]" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Privacy &amp; Data</h2>
            </div>
            <p className="text-[#A3A3A3] mb-5">Manage your marketing preferences and your POPIA data rights. See our <a href="/privacy" className="text-[#C0975C] underline">Privacy Notice</a>.</p>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-[#C0975C]" />
                  <div>
                    <p className="font-bold">Marketing emails</p>
                    <p className="text-sm text-[#A3A3A3]">Occasional financial tips and updates</p>
                  </div>
                </div>
                <button onClick={toggleMarketing} disabled={privacyBusy} data-testid="marketing-toggle"
                  className={`px-4 py-2 font-bold text-sm rounded-lg transition-colors ${privacy.marketing_consent ? 'bg-[#C0975C] text-[#0A1F44] hover:opacity-90' : 'bg-white/10 text-white hover:bg-white/20'} ${privacyBusy ? 'opacity-50' : ''}`}>
                  {privacy.marketing_consent ? 'On' : 'Off'}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => requestData('export')} disabled={privacyBusy} data-testid="data-export-btn"
                  className="py-2.5 text-sm font-semibold rounded-lg border border-[#1D3D72] text-white hover:bg-white/5 transition-colors disabled:opacity-50">
                  Request my data
                </button>
                <button onClick={() => requestData('delete')} disabled={privacyBusy} data-testid="data-delete-btn"
                  className="py-2.5 text-sm font-semibold rounded-lg border border-[#FF3333]/40 text-[#FF6B6B] hover:bg-[#FF3333]/10 transition-colors disabled:opacity-50">
                  Request account deletion
                </button>
              </div>

              {privacy.requests && privacy.requests.length > 0 && (
                <div className="text-xs text-[#A3A3A3]" data-testid="privacy-requests">
                  {privacy.requests.map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-t border-white/5">
                      <span className="capitalize">{r.type} request</span>
                      <span className="uppercase tracking-wider text-[#C0975C]">{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Account Info */}
          <div className="neo-card p-6 mt-8">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Account Info
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-[#A3A3A3]">Email</span>
                <span>{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-[#A3A3A3]">Age</span>
                <span>{user?.age} years old</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-[#A3A3A3]">Member Since</span>
                <span>{new Date(user?.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-[#A3A3A3]">Quizzes Completed</span>
                <span className="text-[#C0975C] font-bold">{user?.total_quizzes_completed || 0}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
